/**
 * Export / import custom indicator projects as .vybeind JSON bundles.
 * Full bundles include source; binary bundles include compiled assembly only.
 */

import {
  createNewIndicatorId,
  deleteProject,
  getBuild,
  getFile,
  getProject,
  listFiles,
  listProjects,
  saveBuild,
  saveProjectWithFiles,
} from "./db";
import { byteSize, checkLimits } from "./limits";
import { normalizeProject } from "./project-ownership";
import {
  DEFAULT_CSPROJ_PATH,
  DEFAULT_ENTRY_FILE,
  DEFAULT_README_FILE,
} from "./project-fs";
import type { CompileResult } from "./runtime-types";
import type {
  CustomIndicatorBuild,
  CustomIndicatorProject,
  ProjectFileNode,
  VybeIndicatorBundle,
  VybeIndicatorBundleKind,
} from "./types";

export { VYBE_IND_BUNDLE_VERSION } from "./types";

export interface ImportBundleConflict {
  type: "name";
  existingProjectId: string;
  existingName: string;
}

export type ImportBundleResult =
  | { ok: true; projectId: string }
  | { ok: false; conflict: ImportBundleConflict }
  | { ok: false; error: string };

function sanitizeFileName(name: string): string {
  return name.replace(/[^\w\s.-]/g, "").trim() || "indicator";
}

function bundleKindOf(bundle: VybeIndicatorBundle): VybeIndicatorBundleKind {
  return bundle.bundleKind === "binary" ? "binary" : "full";
}

export async function exportProjectBundle(
  projectId: string
): Promise<{ blob: Blob; filename: string }> {
  const project = await getProject(projectId);
  if (!project) throw new Error("Project not found");
  if (!project.hasSource || project.readOnly) {
    throw new Error("This indicator cannot be exported with source code");
  }

  const files = await listFiles(projectId);
  const build = (await getBuild(projectId)) ?? null;
  const readme = await getFile(projectId, DEFAULT_README_FILE);

  const bundle: VybeIndicatorBundle = {
    formatVersion: 1,
    bundleKind: "full",
    exportedAt: Date.now(),
    project: { ...normalizeProject(project) },
    files: files.map((f) => ({
      path: f.path,
      type: f.type,
      content: f.content,
    })),
    build,
    readmeMarkdown: readme?.content,
  };

  const json = JSON.stringify(bundle);
  const blob = new Blob([json], { type: "application/json" });
  const filename = `${sanitizeFileName(project.name)}.vybeind`;
  return { blob, filename };
}

export async function exportProjectBinaryBundle(
  projectId: string
): Promise<{ blob: Blob; filename: string }> {
  const project = await getProject(projectId);
  if (!project) throw new Error("Project not found");
  const build = await getBuild(projectId);
  if (!build?.assemblyBase64) {
    throw new Error("No compiled build found — compile the project first");
  }

  const readme = await getFile(projectId, DEFAULT_README_FILE);
  const bundle: VybeIndicatorBundle = {
    formatVersion: 1,
    bundleKind: "binary",
    exportedAt: Date.now(),
    project: {
      ...normalizeProject(project),
      entryContent: undefined,
      hasSource: false,
      readOnly: true,
    },
    files: [],
    build,
    readmeMarkdown: readme?.content,
  };

  const json = JSON.stringify(bundle);
  const blob = new Blob([json], { type: "application/json" });
  const filename = `${sanitizeFileName(project.name)}.vybeind`;
  return { blob, filename };
}

export function downloadBundle(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function parseBundleJson(text: string): VybeIndicatorBundle {
  const data = JSON.parse(text) as VybeIndicatorBundle;
  if (!data || typeof data !== "object") {
    throw new Error("Invalid bundle file");
  }
  if (data.formatVersion !== 1) {
    throw new Error(`Unsupported bundle version: ${data.formatVersion}`);
  }
  if (!data.project?.name) {
    throw new Error("Bundle missing project metadata");
  }
  const kind = bundleKindOf(data);
  if (kind === "binary") {
    if (!data.build?.assemblyBase64) {
      throw new Error("Binary bundle missing compiled assembly");
    }
  } else if (!data.files?.length) {
    throw new Error("Bundle missing source files");
  }
  return data;
}

export async function findImportNameConflict(
  name: string,
  excludeProjectId?: string
): Promise<ImportBundleConflict | null> {
  const projects = await listProjects();
  const normalized = name.trim().toLowerCase();
  const match = projects.find(
    (p) =>
      p.id !== excludeProjectId &&
      p.name.trim().toLowerCase() === normalized
  );
  if (!match) return null;
  return {
    type: "name",
    existingProjectId: match.id,
    existingName: match.name,
  };
}

function validateBundleSize(
  files: VybeIndicatorBundle["files"]
): string | null {
  const pseudoNodes: ProjectFileNode[] = files.map((f) => ({
    id: `bundle:${f.path}`,
    projectId: "bundle",
    path: f.path,
    type: f.type,
    content: f.content,
    sizeBytes: f.type === "file" ? byteSize(f.content) : 0,
    createdAt: 0,
    updatedAt: 0,
  }));
  let total = 0;
  for (const f of files) {
    if (f.type !== "file") continue;
    const limit = checkLimits({
      content: f.content,
      existingFiles: pseudoNodes.filter((n) => n.path !== f.path),
      targetPath: f.path,
      isNewNode: false,
    });
    if (!limit.ok) return limit.error ?? "File too large";
    total += byteSize(f.content);
  }
  if (total > 5 * 1024 * 1024) return "Project exceeds 5 MB limit";
  if (files.length > 200) return "Project exceeds 200 file limit";
  return null;
}

function ownershipForImport(
  bundle: VybeIndicatorBundle
): Pick<
  CustomIndicatorProject,
  "owner" | "source" | "readOnly" | "hasSource" | "publisher"
> {
  if (bundleKindOf(bundle) === "binary") {
    return {
      owner: "external",
      source: "imported-binary",
      readOnly: true,
      hasSource: false,
      publisher: bundle.project.publisher ?? "External",
    };
  }
  return {
    owner: "external",
    source: "imported-source",
    readOnly: false,
    hasSource: true,
    publisher: bundle.project.publisher ?? "External",
  };
}

export async function importProjectBundle(
  bundle: VybeIndicatorBundle,
  options: { overwriteProjectId?: string } = {}
): Promise<ImportBundleResult> {
  const isBinary = bundleKindOf(bundle) === "binary";
  if (!isBinary) {
    const sizeError = validateBundleSize(bundle.files);
    if (sizeError) return { ok: false, error: sizeError };
  }

  const targetName = bundle.project.name.trim();
  if (!options.overwriteProjectId) {
    const conflict = await findImportNameConflict(targetName);
    if (conflict) return { ok: false, conflict };
  }

  const projectId = options.overwriteProjectId ?? createNewIndicatorId();
  const now = Date.now();

  if (options.overwriteProjectId) {
    await deleteProject(options.overwriteProjectId);
  }

  const ownership = ownershipForImport(bundle);

  const project: CustomIndicatorProject = normalizeProject({
    ...bundle.project,
    ...ownership,
    id: projectId,
    name: targetName || bundle.project.name,
    entryFilePath: bundle.project.entryFilePath ?? DEFAULT_ENTRY_FILE,
    csprojFilePath: bundle.project.csprojFilePath ?? DEFAULT_CSPROJ_PATH,
    installedAt: isBinary ? now : bundle.project.installedAt,
    createdAt: options.overwriteProjectId
      ? bundle.project.createdAt
      : now,
    updatedAt: now,
  });

  if (isBinary) {
    project.entryContent = undefined;
    project.sizeBytes = bundle.readmeMarkdown
      ? byteSize(bundle.readmeMarkdown)
      : 0;
    await saveProjectWithFiles(project, [
      ...(bundle.readmeMarkdown
        ? [
            {
              path: DEFAULT_README_FILE,
              content: bundle.readmeMarkdown,
              type: "file" as const,
            },
          ]
        : []),
    ]);
  } else {
    const fileUpdates = bundle.files.map((f) => ({
      path: f.path,
      content: f.content,
      type: f.type,
    }));

    if (bundle.readmeMarkdown) {
      const hasReadme = fileUpdates.some((f) => f.path === DEFAULT_README_FILE);
      if (!hasReadme) {
        fileUpdates.push({
          path: DEFAULT_README_FILE,
          content: bundle.readmeMarkdown,
          type: "file",
        });
      }
    }

    project.sizeBytes = fileUpdates
      .filter((f) => f.type === "file")
      .reduce((s, f) => s + byteSize(f.content), 0);

    const entry = fileUpdates.find(
      (f) => f.path === project.entryFilePath && f.type === "file"
    );
    if (entry) project.entryContent = entry.content;

    await saveProjectWithFiles(project, fileUpdates);
  }

  if (bundle.build?.assemblyBase64) {
    const build: CustomIndicatorBuild = {
      ...bundle.build,
      projectId,
      builtAt: bundle.build.builtAt ?? now,
    };
    await saveBuild(build);
  }

  return { ok: true, projectId };
}

/** Build meta from bundle when no persisted DLL (import still works, must recompile). */
export function bundleBuildToCompileResult(
  build: CustomIndicatorBuild | null | undefined
): CompileResult | undefined {
  if (!build?.meta || !build.assemblyBase64) return undefined;
  return {
    success: true,
    logs: [],
    ...build.meta,
    assemblyBase64: build.assemblyBase64,
    inputs: build.meta.inputs ?? [],
    plots: build.meta.plots ?? [],
  };
}
